import { useState, useEffect } from "react";

/**
 * useNavThemeFlip — tracks whether the landing nav currently overlaps a dark
 * section, so the navbar can flip its theme over those sections.
 *
 * Mirrors the design handoff's _initNav (Altair Homepage.dc.html L514-550):
 * a dark section is "active" when its getBoundingClientRect satisfies
 * top <= 32 && bottom >= 32 (i.e. it crosses the line just below the nav top).
 *
 * - When `selector` is undefined, the hook is inert and always returns false
 *   (existing non-homepage callers behave exactly as before).
 * - Uses rAF-throttled, passive scroll + resize listeners with full cleanup.
 * - React-Compiler safe: all mutable bookkeeping lives inside the effect.
 */
export function useNavThemeFlip(selector?: string): boolean {
  const [dark, setDark] = useState(false);

  useEffect(() => {
    if (!selector) return;

    let rafId: number | null = null;
    let lastDark: boolean | null = null;

    const update = () => {
      rafId = null;
      const probe = 32; // just below the nav top edge
      const sections = document.querySelectorAll(selector);
      let onDark = false;
      for (const s of sections) {
        const r = s.getBoundingClientRect();
        if (r.top <= probe && r.bottom >= probe) {
          onDark = true;
          break;
        }
      }
      if (onDark !== lastDark) {
        lastDark = onDark;
        setDark(onDark);
      }
    };

    const schedule = () => {
      if (rafId === null) rafId = requestAnimationFrame(update);
    };

    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule, { passive: true });
    update();

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [selector]);

  return dark;
}
